// Every enumeration here is a runtime array rather than a bare type union: a
// file arriving from outside has to be checked against the allowed values, and
// a type union does not exist at runtime to check against.
export const PRICE_FIELDS = ["open", "high", "low", "close"];
export const OPERAND_KINDS = ["indicator", "price", "const"];
/**
 * Furthest back an operand may look. A live bot keeps this much more history
 * in its buffer; past a few hundred bars that buffer is the cost, and no
 * pattern a rule describes needs it.
 */
export const MAX_OPERAND_OFFSET = 500;
/** Bars back an operand reads; 0 for the current bar and for constants. */
export function operandOffset(operand) {
    if (operand.kind === "const")
        return 0;
    const offset = operand.offset;
    return typeof offset === "number" && Number.isInteger(offset) && offset > 0 ? offset : 0;
}
export const COMPARISON_OPS = ["gt", "lt", "crosses_above", "crosses_below"];
export const GROUP_OPS = ["and", "or"];
/**
 * TypeScript cannot narrow `ConditionNode` on `node.op === "and"`, because the
 * discriminant on the group variant is a union rather than a single literal.
 * An explicit guard narrows reliably everywhere this is used.
 */
export function isConditionGroup(node) {
    return node.op === "and" || node.op === "or";
}
export function emptyConditionGroup(op = "and") {
    return { op, children: [] };
}
export const POSITION_SIZE_MODES = ["pct_equity", "fixed_quote", "fixed_units"];
/**
 * Which way a strategy trades when its entry condition fires.
 *
 * One direction per strategy rather than separate long and short condition
 * trees: the same rule read in reverse is almost never the rule you want, and a
 * single tree keeps "what does this strategy do" answerable at a glance. To
 * trade both ways, save two strategies and benchmark them together.
 */
export const TRADE_DIRECTIONS = ["long", "short"];
/** Direction, defaulted. A spec that names none is long. */
export function effectiveDirection(spec) {
    return spec.direction ?? "long";
}
/** Leverage, defaulted and floored at 1. A spec that names none is unleveraged. */
export function effectiveLeverage(risk) {
    return Math.max(1, risk.leverage ?? 1);
}
export function defaultRiskParams() {
    return { sizeMode: "pct_equity", sizeValue: 100, leverage: 1, feePct: 0, slippagePct: 0 };
}
/**
 * Fills in the optional execution fields with the unleveraged, frictionless
 * defaults, so downstream code reads concrete numbers rather than `?? 0` at
 * every use.
 */
export function normalizeRiskParams(risk) {
    if (!risk)
        return defaultRiskParams();
    return {
        sizeMode: risk.sizeMode ?? "pct_equity",
        sizeValue: risk.sizeValue ?? 100,
        stopLossPct: risk.stopLossPct,
        takeProfitPct: risk.takeProfitPct,
        trailingStopPct: risk.trailingStopPct,
        leverage: Math.max(1, risk.leverage ?? 1),
        feePct: risk.feePct ?? 0,
        slippagePct: risk.slippagePct ?? 0,
        applyFunding: risk.applyFunding ?? false,
    };
}
/**
 * Which engine profile a strategy was authored against.
 *
 * `research` is everything the backtester models. `deployable` is the subset a
 * live bot will actually execute — it has no leverage model, no funding
 * settlement and no second synchronised feed, so a strategy using those is
 * honest research and cannot be shipped.
 *
 * The profile *definitions* live in `@strat/engine`, because a profile is a
 * claim about what an engine will execute and has to derive from the constants
 * the engine actually reads. Only the identifier is part of the file format —
 * a published strategy declares which profile it was written for, and a
 * marketplace filters on it.
 */
export const PROFILE_IDS = ["research", "deployable"];
//# sourceMappingURL=spec.js.map